import React, { useState } from "react";
import useRevealSequence from "../useRevealSequence";
import Celebration from "../Celebration";

/**
 * Flip Cards - the board face down, then turned over one at a time.
 *
 * The cards shuffle while the round finishes. Once the result is in they
 * turn over in order, each one a little slower than the last, and the
 * winner is left for the end: the last card to turn is the one that won.
 */

const SHUFFLE_MS = 420;

export default function FlipCards({ tiles, winnerIndex, onFinished }) {
  const count = tiles.length;
  const [shuffling, setShuffling] = useState(true);
  const [flipped, setFlipped] = useState([]);
  const [landed, setLanded] = useState(-1);

  useRevealSequence(
    async (ctx) => {
      // Face down and jostling until the round is over.
      while (ctx.winnerIndex() < 0) {
        setShuffling(true);
        await ctx.sleep(SHUFFLE_MS);
        setShuffling(false);
        await ctx.sleep(SHUFFLE_MS);
      }
      const target = ctx.winnerIndex();
      setShuffling(false);

      // Everything but the winner, in board order, slowing down.
      const order = tiles
        .map((_, i) => i)
        .filter((i) => i !== target);

      for (let n = 0; n < order.length; n++) {
        const t = n / Math.max(1, order.length);
        setFlipped((prev) => [...prev, order[n]]);
        await ctx.sleep(34 + 70 * t * t);
      }

      await ctx.sleep(140);
      setFlipped((prev) => [...prev, target]);
      await ctx.sleep(240);
      setLanded(target);
      await ctx.sleep(1200);
      onFinished();
    },
    winnerIndex,
    [count],
  );

  const winner = landed >= 0 ? tiles[landed] : null;

  return (
    <div className="rv-flip">
      <div className="rv-flip-grid" data-count={count}>
        {tiles.map((tile, i) => {
          const isOpen = flipped.includes(i);
          const isWinner = landed === i;
          return (
            <div
              key={tile.id}
              className={[
                "rv-flip-card",
                isOpen ? "rv-flip-card--open" : "",
                isWinner ? "rv-flip-card--winner" : "",
                landed >= 0 && !isWinner ? "rv-flip-card--dim" : "",
                shuffling ? "rv-flip-card--shuffle" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ "--rv-delay": `${(i % 6) * 60}ms` }}
            >
              <div className="rv-flip-inner">
                <div className="rv-flip-back">
                  <span>❖</span>
                </div>
                <div className="rv-flip-front">
                  <img src={tile.img} alt={tile.name || ""} />
                  <span>{tile.name}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {winner ? <Celebration tile={winner} seed={landed} /> : null}
    </div>
  );
}
